/**
 * Single source of truth: the `TextLayer.fontFamily` field holds the canonical
 * first font name. Rendering and serialization derive the full CSS
 * `font-family` value (primary + fallback chain) from that name on the fly.
 *
 * Storing a stack alongside the name was fragile — every code path that
 * mutated `fontFamily` had to remember to clear the stack, and one missed
 * spot left the old stack live (so the rendered element kept the old font
 * even though the dropdown reflected the new choice).
 */

const SERIF_PATTERN =
  /\b(garamond|georgia|times|didot|bodoni|baskerville|cambria|caslon|cormorant|merriweather|playfair|lora|crimson|serif)\b/i;
const MONO_PATTERN =
  /\b(courier|mono|consolas|monaco|menlo|fira\s*code|jetbrains)\b/i;

/**
 * A bare (unquoted) token in a CSS `font-family` value must be a valid
 * `<custom-ident>`: ASCII letters/digits/hyphen/underscore, not starting with a
 * digit (an optional single leading hyphen is allowed, e.g. `-apple-system`).
 * A `.` is not allowed unquoted — and brand fonts arrive as hashed filenames
 * like `apercu-bold.e25a8b88`, so emitting them bare made the browser reject the
 * entire `font-family` declaration (fallbacks included) and silently drop to the
 * inherited font. The grid thumbnail looked fine only because it renders the
 * saved markup verbatim, where the name is still quoted.
 *
 * Non-ASCII family names don't match and so get quoted — that's harmless, since
 * a quoted family name is always valid CSS and resolves identically.
 */
const SAFE_BARE_FAMILY = /^-?[A-Za-z_][A-Za-z0-9_-]*$/;

/**
 * Quote a font-family name unless it is a provably-safe bare identifier.
 * Quoting is always valid CSS, so anything that isn't a clean `<custom-ident>`
 * (dotted hash names, names with spaces or embedded quotes, leading digits, the
 * empty string) gets quoted. Generic keywords like `sans-serif` are valid idents
 * and stay bare — quoting them would turn the generic into a lookup for a font
 * literally named "sans-serif".
 */
export function quoteFamily(name: string): string {
  if (SAFE_BARE_FAMILY.test(name)) return name;
  return `"${name.replace(/"/g, '\\"')}"`;
}

/**
 * Build a CSS `font-family` value with a category-appropriate fallback chain.
 * Heuristic-classifies the primary name as serif / monospace / sans-serif and
 * appends a stack that keeps metrics close enough to avoid silent line
 * rewrapping when the primary isn't installed.
 */
export function fontFamilyStackFor(fontFamily: string): string {
  const primary = quoteFamily(fontFamily);
  if (SERIF_PATTERN.test(fontFamily)) return `${primary}, Georgia, serif`;
  if (MONO_PATTERN.test(fontFamily)) {
    return `${primary}, ui-monospace, Menlo, monospace`;
  }
  return `${primary}, "Helvetica Neue", Arial, sans-serif`;
}

/**
 * Extract the canonical first font name from a raw `font-family` value, with
 * surrounding quotes stripped. Falls back to `fallback` when the value carries
 * no name — "Inter" (the renderer's default) unless a caller has a better one.
 */
export function parseFontFamilyName(raw: string, fallback = "Inter"): string {
  if (!raw) return fallback;
  return (
    raw
      .split(",")[0]
      ?.trim()
      .replace(/^['"]|['"]$/g, "") || fallback
  );
}

/**
 * Font families every ad render surface can resolve without loading brand
 * assets: the OS-bundled classics plus Inter (the renderer's default) and
 * Helvetica Neue (present in the fallback stacks above). Shared by the editor
 * font picker and the ads API font catalog so the two can't drift — a font
 * offered in the picker must never be rejected by the API's write preflight.
 */
export const WEB_SAFE_FONTS = [
  // Sans-serif
  "Inter",
  "Arial",
  "Helvetica",
  "Helvetica Neue",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Segoe UI",
  "Gill Sans",
  // Serif
  "Times New Roman",
  "Georgia",
  "Garamond",
  "Palatino",
  // Monospace
  "Courier New",
  "Consolas",
  "Monaco",
  // Display
  "Impact",
  "Comic Sans MS",
] as const;
